import React, { useLayoutEffect } from 'react';

import { SettingsProvider } from '~/app/providers';
import { HandleBroadcastEvents, ProlongateSession, useAuth } from '~/features/auth';
import { useScrollRestoration } from '~/shared';
import { PageFragment } from '~/shared/api/graphql.fragments';
import { Container, IF } from '~/shared/components';
import { FooterSection } from '~/widgets/footer';
import { Header } from '~/widgets/header';

import * as styles from './layout.module.scss';

/**
 * Renders the public layout for the application.
 *
 * @param {Object} location - the location object
 * @param {Object} pageContext - the page context object
 * @param {ReactNode} children - the child components to be rendered
 * @param {string} dataTheme - theme name
 * @return {React.JSX.Element} - the public layout component
 */
export const PublicLayout = ({ location, pageContext, children, dataTheme = 'default' }) => {
  const { syncJwt, isLoggedIn, isPending } = useAuth();
  const { restoreScroll } = useScrollRestoration({ ignorePagesWithPrefix: '/app' });

  useLayoutEffect(() => {
    syncJwt();
    restoreScroll();
    if (typeof document !== 'undefined') {
      document.body.setAttribute('data-theme', dataTheme);
      document.documentElement.setAttribute('data-theme', dataTheme);
    }
  }, [dataTheme]);

  if (isPending) return <></>;
  return (
    <>
      <PageFragment />
      <HandleBroadcastEvents channel={'login_channel'} loginAction={syncJwt} logoutAction={syncJwt} />
      <Container className={styles.container} data-theme={dataTheme}>
        <SettingsProvider>
          <Header pageContext={pageContext} location={location} />
          <main className={'content-grid'}>{children}</main>
          <FooterSection />
        </SettingsProvider>
      </Container>

      <IF condition={isLoggedIn}>
        <ProlongateSession action={syncJwt} interval={60 * 1000} />
      </IF>
    </>
  );
};
